import { useEffect, useState } from 'react';
import { Flame, X } from 'lucide-react';

/**
 * Full-screen celebration overlay shown the moment a play pushes the streak
 * past a badge milestone (3/7/30/100 days). Named after the tier, styled to
 * match the tier's heat, with an ember burst behind the badge.
 */
const TIERS: Record<number, { label: string; frame: string; text: string; glow: string }> = {
  3: {
    label: '3-DAY',
    frame: 'border-primary bg-primary/10',
    text: 'text-primary',
    glow: 'shadow-[0_0_60px_rgba(0,217,255,0.4)]',
  },
  7: {
    label: '7-DAY',
    frame: 'border-secondary bg-secondary/10',
    text: 'text-secondary',
    glow: 'shadow-[0_0_60px_rgba(168,85,247,0.45)]',
  },
  30: {
    label: '30-DAY',
    frame: 'border-amber-400 bg-amber-400/10',
    text: 'text-amber-300',
    glow: 'shadow-[0_0_60px_rgba(251,191,36,0.5)]',
  },
  100: {
    label: '100-DAY',
    frame: 'border-red-500 bg-red-500/10',
    text: 'text-red-400',
    glow: 'shadow-[0_0_70px_rgba(239,68,68,0.55)]',
  },
};

const EMBERS = Array.from({ length: 14 }, (_, i) => ({
  left: `${6 + ((i * 37) % 88)}%`,
  delay: `${(i % 7) * 0.18}s`,
  duration: `${1.6 + (i % 5) * 0.35}s`,
  size: i % 3 === 0 ? 'h-2 w-2' : 'h-1.5 w-1.5',
}));

export function BadgeUnlockCelebration({
  milestones,
  onDismiss,
}: {
  milestones: number[];
  onDismiss: () => void;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Mount hidden, then flip visible so the entrance animation plays.
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  if (milestones.length === 0) return null;
  const sorted = [...milestones].sort((a, b) => a - b);
  // Celebrate the highest tier front-and-center; list the rest below.
  const top = sorted[sorted.length - 1];
  const tier = TIERS[top] ?? {
    label: `${top}-DAY`,
    frame: 'border-primary bg-primary/10',
    text: 'text-primary',
    glow: 'shadow-[0_0_60px_rgba(0,217,255,0.4)]',
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      data-testid="overlay-badge-unlock"
      role="dialog"
      aria-modal="true"
      aria-label={`${tier.label} streak badge unlocked`}
      onClick={onDismiss}
    >
      <div className="absolute inset-0 bg-background/90 backdrop-blur-sm" />

      {/* Rising embers */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {EMBERS.map((e, i) => (
          <span
            key={i}
            className={`absolute bottom-[-10px] rounded-full ${e.size} ${tier.text} bg-current opacity-0 animate-ember-rise`}
            style={{ left: e.left, animationDelay: e.delay, animationDuration: e.duration }}
          />
        ))}
      </div>

      <div
        className={`relative w-full max-w-md border-2 p-10 text-center ${tier.frame} ${tier.glow} ${
          visible ? 'animate-badge-burst' : ''
        }`}
        onClick={(ev) => ev.stopPropagation()}
      >
        <button
          onClick={onDismiss}
          className="absolute right-3 top-3 text-muted-foreground transition-colors hover:text-white"
          aria-label="Dismiss"
          data-testid="button-dismiss-badge-unlock"
        >
          <X className="h-5 w-5" />
        </button>

        <div className={`mx-auto mb-6 inline-flex h-24 w-24 items-center justify-center border-2 ${tier.frame} animate-badge-flame`}>
          <Flame className={`h-12 w-12 ${tier.text}`} />
        </div>

        <p className={`mb-2 font-mono text-xs uppercase tracking-widest ${tier.text} animate-pulse`}>
          BADGE UNLOCKED
        </p>
        <h2
          className={`mb-4 font-display text-5xl font-black uppercase tracking-widest ${tier.text}`}
          data-testid="text-unlocked-badge-name"
        >
          {tier.label}
        </h2>
        <p className="mb-6 font-mono text-xs uppercase tracking-widest text-muted-foreground">
          {top}-day streak milestone reached
        </p>

        {sorted.length > 1 && (
          <p className="mb-6 font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="text-additional-badges">
            ALSO UNLOCKED: {sorted.slice(0, -1).map((m) => (TIERS[m]?.label ?? `${m}-DAY`)).join(' + ')}
          </p>
        )}

        <button
          onClick={onDismiss}
          className={`w-full border-2 py-3 font-bold font-mono text-sm uppercase tracking-widest transition-all hover:brightness-125 ${tier.frame} ${tier.text}`}
          data-testid="button-claim-badge"
        >
          KEEP THE STREAK ALIVE
        </button>
      </div>
    </div>
  );
}
